// Copyright (c) Bentley Systems
import * as React from "react";
import { EtNode } from "../DataModels/et-node";
import { ActionVm } from "../../Action/action-vm";
import { ActionParameterVm } from "../../Action/action-parameter-vm";
import { ConfirmationText } from "../../Action/confirmation-text";
import { Style } from "../../DataModels/style";

export type UseEtreeActionsResult = {
  headerActions: ActionVm[];
  headerBackAction: ActionVm | undefined;
  itemActionsMap: Map<number, ActionVm>;
  buildHeaderActions: (config: EtNode) => void;
  buildItemActions: (config: EtNode) => Map<number, ActionVm>;
};

// Fallback caption check: treat actions labelled "back" as back-header buttons
// when the explicit f_BACK flag is not set.
const BACK_BUTTON_CAPTION = "back";

const parseIconName = (iconName: string | undefined): string | undefined => {
  if (!iconName) return undefined;
  const str = "itwinicon:";
  const data = iconName.toLowerCase();
  if (data.indexOf(str) > -1) {
    return data.substring(str.length).trim();
  }
  return iconName;
};

/**
 * Builds header-level and node-level action maps from a tree configuration.
 *
 * Handles:
 * - Parsing `etActions` from the tree config into `ActionVm` instances
 * - Applying sorting, icon resolution, confirmation text, and style overrides
 * - Separating header actions (type "H") from item actions (type "O")
 * - Computing visibility conditions based on node field values
 *
 * @param treeConfig - Active tree configuration containing action definitions
 * @returns Object with `headerActions`, `headerBackAction`, `itemActionsMap`,
 *   `buildHeaderActions` (call on config load), and `buildItemActions` (call on config load)
 */
export const useEtreeActions = (treeConfig: EtNode | null): UseEtreeActionsResult => {
  const [headerActions, setHeaderActions] = React.useState<ActionVm[]>([]);
  const [headerBackAction, setHeaderBackAction] = React.useState<ActionVm | undefined>(
    undefined
  );
  const [itemActionsMap, setItemActionsMap] = React.useState<Map<number, ActionVm>>(
    new Map()
  );

  React.useEffect(() => {
    if (treeConfig) return;
    setHeaderActions([]);
    setHeaderBackAction(undefined);
    setItemActionsMap(new Map());
  }, [treeConfig]);

  const buildHeaderActions = React.useCallback((config: EtNode) => {
    const actions: ActionVm[] = [];

    // Prevent stale back button when switching to a tree without header actions.
    setHeaderBackAction(undefined);

    if (!config.etActions) {
      setHeaderActions(actions);
      return;
    }

    config.etActions
      .filter((a) => a.actionType === "H")
      .sort((a, b) => (a.actionOrder || 0) - (b.actionOrder || 0))
      .forEach((etAction) => {
        const action = new ActionVm();
        action.caption =
          etAction.etActionTexts !== undefined && etAction.etActionTexts.length > 0
            ? etAction.etActionTexts[0].text
            : etAction.caption || `Action ${etAction.actionId}`;
        action.actionId = etAction.actionId;

        const confirmation = etAction.confirmation;
        if (
          confirmation &&
          confirmation.confirmationText
        ) {
          action.hasConfirmation = true;
          action.confirmationText = new ConfirmationText(confirmation.confirmationText);
        } else if (etAction.confirmationType === "Y") {
          action.hasConfirmation = true;
          if (etAction.confirmationText != null) {
            action.confirmationText = new ConfirmationText(etAction.confirmationText);
          }
        }

        action.targetNodeId = etAction.targetNodeIdResolved;
        action.targetNodeType = etAction.targetNodeType;
        action.targetNodeUniqueName = etAction.targetNodeUniqueName;
        action.f_ROLL = etAction.f_ROLL;
        action.isBackHeaderButton = etAction.f_BACK || false;

        if (!action.isBackHeaderButton && action.caption.toLowerCase() === BACK_BUTTON_CAPTION) {
          action.isBackHeaderButton = true;
        }

        if (etAction.styleId !== undefined && etAction.style) {
          const styleVm = new Style();
          styleVm.styleId = etAction.styleId.toString();
          styleVm.data = etAction.style.data;
          styleVm.backgroundImageBase64Str = etAction.style.backgroundImageBase64Str;
          styleVm.customData = etAction.style.customData;
          action.style = styleVm;
        }

        if (etAction.etActionParams) {
          etAction.etActionParams.forEach((p) => {
            const par = new ActionParameterVm();
            par.actionParamId = p.actionParamId;
            par.name = p.name || "";
            par.value = p.value || "";
            par.valueType = p.valueType || "";
            action.actionParameters.push(par);
          });
        }

        if (!action.isBackHeaderButton) {
          actions.push(action);
        } else {
          setHeaderBackAction(action);
        }
      });

    setHeaderActions(actions);
  }, []);

  const buildItemActions = React.useCallback((config: EtNode): Map<number, ActionVm> => {
    const actionsMap = new Map<number, ActionVm>();

    if (!config.etActions) {
      setItemActionsMap(actionsMap);
      return actionsMap;
    }

    const itemActions = config.etActions.filter((a) => a.actionType === "O");

    itemActions.forEach((etAction) => {
      const action = new ActionVm();
      action.caption =
        etAction.etActionTexts !== undefined && etAction.etActionTexts.length > 0
          ? etAction.etActionTexts[0].text
          : etAction.caption || `Action ${etAction.actionId}`;
      action.actionId = etAction.actionId;

      const confirmation = etAction.confirmation;
      if (confirmation && confirmation.confirmationText) {
        action.hasConfirmation = true;
        action.confirmationText = new ConfirmationText(confirmation.confirmationText);
      } else if (etAction.confirmationType === "Y") {
        action.hasConfirmation = true;
        if (etAction.confirmationText != null) {
          action.confirmationText = new ConfirmationText(etAction.confirmationText);
        }
      }

      action.targetNodeId = etAction.targetNodeIdResolved;
      action.targetNodeType = etAction.targetNodeType;
      action.targetNodeUniqueName = etAction.targetNodeUniqueName;
      action.f_ROLL = etAction.f_ROLL;
      action.isItemAction = true;

      if (etAction.icon) {
        action.icon = parseIconName(etAction.icon.name);
        action.iconBase64Str = etAction.icon.base64Str;
      }

      if (etAction.styleId !== undefined && etAction.style) {
        const styleVm = new Style();
        styleVm.styleId = etAction.styleId.toString();
        styleVm.data = etAction.style.data;
        styleVm.backgroundImageBase64Str = etAction.style.backgroundImageBase64Str;
        styleVm.customData = etAction.style.customData;
        action.style = styleVm;
      }

      if (etAction.etActionParams) {
        etAction.etActionParams.forEach((p) => {
          const par = new ActionParameterVm();
          par.actionParamId = p.actionParamId;
          par.name = p.name || "";
          par.value = p.value || "";
          par.valueType = p.valueType || "";
          action.actionParameters.push(par);
        });
      }

      action.visCompareValue = etAction.visCompareValue;
      action.visCompareType = etAction.visCompareType;
      action.visFromValue = etAction.visFromValue;
      action.visFromType = etAction.visFromType;
      action.visToValue = etAction.visToValue;
      action.visToType = etAction.visToType;

      actionsMap.set(action.actionId, action);
    });

    setItemActionsMap(actionsMap);
    return actionsMap;
  }, []);

  return {
    headerActions,
    headerBackAction,
    itemActionsMap,
    buildHeaderActions,
    buildItemActions,
  };
};
